import {z} from 'zod'
import {PatientExtended} from '@/features/patients/types'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {useOutletContext} from 'react-router-dom'
import {WarningBanner} from '@/components/commons/warning-banner/WarningBanner'
import {InfoBanner} from '@/components/commons/info-banner/InfoBanner'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSelectProjectByPatient} from '@/features/projects/components/input-select-project-by-patient/InputSelectProjectByPatient'
import {MedicalReportFieldsSchema} from '../../formSchemas'
import {MedicalReportProjectCard} from '../medical-report-project-card/MedicalReportProjectCard'
import {useTranslation} from '@/translations/i18n'
import {useEffect, useMemo} from 'react'
import {MedicalReportMrpCard} from '@/features/medical-reports/components/medical-report-mrp-card/MedicalReportMrpCard'
import {MedicalReportExtendedProjectFields} from '@/features/medical-reports/components/medical-report-extended-project-fields/MedicalReportExtendedProjectFields'
import {ProjectByPatient} from '@/features/projects/types'
import {Switch} from '@/components/commons/switch/Switch'
import {useGetProject} from '@/features/projects/queries/useGetProject'

const MedicalReportProjectSchema = MedicalReportFieldsSchema.pick({
    project: true,
    projectId: true,
    reportType: true,
    noProject: true
})
type MedicalReportProjectSchema = z.infer<typeof MedicalReportProjectSchema>

export const MedicalReportProjectSection = ({isDisabled}: {isDisabled: boolean}) => {
    const {t} = useTranslation()
    const form = useFormContext<MedicalReportProjectSchema>()
    const project = useWatch({control: form.control, name: 'project'})
    const projectQuery = useGetProject({
        urlParams: {projectId: project?.id ?? ''},
        params: {responseType: 'small'},
        options: {enabled: !!project?.id}
    })

    // This is used to populate the project fields when the project is not in the form
    useEffect(() => {
        if (project?.id && !projectQuery.isLoading && !projectQuery.isError && projectQuery.data) {
            form.setValue('project', projectQuery.data, {shouldDirty: true})
            form.setValue('projectId', projectQuery.data.id, {shouldDirty: true})
        }
    }, [project?.id, projectQuery.data?.id])

    return (
        <FormSection title={t('commons:reference_project')} hasBottomDivider>
            {project ? (
                <MedicalReportProjectField isDisabled={isDisabled} />
            ) : (
                <WarningBanner
                    title={t('medical_records:error_no_project_opened')}
                    subtitle={t('medical_records:error_no_project_opened_description')}
                />
            )}
        </FormSection>
    )
}

const MedicalReportProjectField = ({isDisabled}: {isDisabled: boolean}) => {
    const {t} = useTranslation()

    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const form = useFormContext<MedicalReportProjectSchema>()

    const project = useWatch({control: form.control, name: 'project'})
    const reportType = useWatch({control: form.control, name: 'reportType'})

    const referringDoctor = useMemo(() => {
        return project?.team?.find(member => member.group == 'referring_doctor')
    }, [project])

    const onChangeProjectSideEffect = (project: ProjectByPatient | null) => {
        form.setValue('projectId', project?.id || null, {shouldDirty: true})
    }

    const noProject = useWatch({control: form.control, name: 'noProject'})

    return (
        <Grid columns={3} rowGap={6}>
            {reportType.keyName == 'document' && (
                <GridItem spanColumn={3}>
                    <Controller
                        control={form.control}
                        name="noProject"
                        render={({field}) => (
                            <Switch
                                checked={field.value}
                                disabled={isDisabled}
                                onChange={field.onChange}
                                size="sm"
                                label={t('medical_records:no_project')}
                            />
                        )}
                    />
                </GridItem>
            )}
            <GridItem spanColumn={2}>
                <Controller
                    control={form.control}
                    name="project"
                    render={({field}) => (
                        <InputSelectProjectByPatient
                            label={t('commons:project_number')}
                            placeholder={t('commons:select_a_project')}
                            patientId={patient.id}
                            value={noProject ? null : project}
                            disabled={
                                reportType.keyName !== 'continuous_project_extension_request' &&
                                reportType.keyName !== 'renew_continuous_project'
                            }
                            onChange={value => {
                                field.onChange(value)
                                onChangeProjectSideEffect(value)
                            }}
                        />
                    )}
                />
            </GridItem>
            {!noProject && (
                <>
                    {!!project && (
                        <GridItem spanColumn={3}>
                            <MedicalReportProjectCard project={project} />
                        </GridItem>
                    )}

                    {!!project && project.type == 'cyclic' && project.transformedProjectId && (
                        <GridItem spanColumn={3}>
                            <InfoBanner title={t('medical_records:project_transformed_banner_title')} />
                        </GridItem>
                    )}

                    {reportType.keyName == 'continuous_project_extension_request' && referringDoctor && (
                        <GridItem spanColumn={3} direction="column" gap={6}>
                            <MedicalReportMrpCard referringDoctor={referringDoctor} />
                            <MedicalReportExtendedProjectFields />
                        </GridItem>
                    )}
                </>
            )}
        </Grid>
    )
}
